import type { PaginatedResponseType, PaginationMetaType } from '~/types/api'
import api from './api'

/** Tipos de pregunta que entiende el constructor y la página pública. */
export type FormFieldTypeType =
  | 'short_text'
  | 'long_text'
  | 'email'
  | 'phone'
  | 'number'
  | 'date'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'yes_no'
  | 'signature'
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'spacer'

export type FormFieldWidthType = 'full' | 'half' | 'third' | 'two_thirds'

/** Columnas que ocupa cada ancho en la rejilla de 6 del formulario. */
export const WIDTH_SPAN: Record<FormFieldWidthType, string> = {
  full: 'sm:col-span-6',
  half: 'sm:col-span-3',
  third: 'sm:col-span-2',
  two_thirds: 'sm:col-span-4'
}

export interface FormFieldType {
  id: string
  type: FormFieldTypeType
  label: string
  description?: string
  placeholder?: string
  required?: boolean
  options?: string[]
  /** Paso al que pertenece. Si falta, se asume el primero. */
  stepId?: string
  /** Ancho en la rejilla de 6 columnas. */
  width?: FormFieldWidthType
}

export interface FormStepType {
  id: string
  title: string
  description?: string
}

export const DEFAULT_STEP_ID = 'step_1'

/** Un formulario siempre tiene al menos un paso, aunque no se usen varios. */
export function normalizeSteps(steps?: FormStepType[] | null): FormStepType[] {
  return steps && steps.length > 0 ? steps : [{ id: DEFAULT_STEP_ID, title: 'Paso 1' }]
}

/** Los campos de un paso. Los campos sin `stepId` son del primero (formularios antiguos). */
export function fieldsOfStep(fields: FormFieldType[], stepId: string, isFirstStep: boolean) {
  return fields.filter(field => (field.stepId ? field.stepId === stepId : isFirstStep))
}

export interface FormSettingsType {
  successMessage?: string
  submitLabel?: string
  notifyEmail?: string
}

export type FormStatusType = 'draft' | 'published' | 'closed'

export interface FormType {
  id: number
  companyId: string
  slug: string
  title: string
  description?: string | null
  status: FormStatusType
  steps?: FormStepType[] | null
  fields: FormFieldType[]
  settings?: FormSettingsType | null
  publishedAt?: string | null
  createdAt: string
  updatedAt: string
  /** Sólo viene en el listado. */
  responsesCount?: number
}

export interface FormResponseType {
  id: number
  formId: number
  answers: Record<string, unknown>
  meta?: Record<string, unknown> | null
  createdAt: string
}

export interface SaveFormPayloadType {
  title: string
  description?: string
  slug?: string
  status?: FormStatusType
  steps?: FormStepType[]
  fields?: FormFieldType[]
  settings?: FormSettingsType
}

export interface ResponsesPageType {
  items: FormResponseType[]
  form: { id: number; title: string; fields: FormFieldType[] }
  meta: PaginationMetaType
}

/** Etiquetas de cada tipo, compartidas por el constructor y el listado. */
export const FIELD_TYPE_LABELS: Record<FormFieldTypeType, string> = {
  short_text: 'Texto corto',
  long_text: 'Párrafo',
  email: 'Correo electrónico',
  phone: 'Teléfono',
  number: 'Número',
  date: 'Fecha',
  select: 'Lista desplegable',
  radio: 'Selección única',
  checkbox: 'Selección múltiple',
  yes_no: 'Sí / No',
  signature: 'Firma',
  heading: 'Título de sección',
  paragraph: 'Texto informativo',
  divider: 'Separador',
  spacer: 'Espacio'
}

/** Los que llevan opciones editables. */
export const FIELD_TYPES_WITH_OPTIONS: FormFieldTypeType[] = ['select', 'radio', 'checkbox']

/** Los bloques de contenido no se responden. */
export const STATIC_FIELD_TYPES: FormFieldTypeType[] = ['heading', 'paragraph', 'divider', 'spacer']

export const isAnswerableField = (field: FormFieldType) => !STATIC_FIELD_TYPES.includes(field.type)

/** Texto plano de una respuesta, para la tabla del admin. */
export const isSignature = (value: unknown): value is string => typeof value === 'string' && value.startsWith('data:image/')

export function formatAnswer(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (isSignature(value)) return 'Firmado'
  if (Array.isArray(value)) return value.map(v => String(v)).join(', ')
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'
  return String(value)
}

/** El enlace que se comparte. Se arma contra el origen actual. */
export const publicFormUrl = (slug: string) => `${window.location.origin}/f/${slug}`

export const formService = {
  async list(params?: { page?: number; limit?: number; title?: string; status?: FormStatusType }) {
    const { data } = await api.get('/forms', { params })
    return data.data as PaginatedResponseType<FormType>
  },

  async get(id: number) {
    const { data } = await api.get(`/forms/${id}`)
    return data.data as FormType
  },

  async create(payload: SaveFormPayloadType) {
    const { data } = await api.post('/forms', payload)
    return data.data as FormType
  },

  async update(id: number, payload: SaveFormPayloadType) {
    const { data } = await api.put(`/forms/${id}`, payload)
    return data.data as FormType
  },

  async delete(id: number) {
    const { data } = await api.delete(`/forms/${id}`)
    return data.data as boolean
  },

  /** Elimina varios formularios de una vez. Los que tienen respuestas se omiten. */
  async bulkDelete(ids: number[]) {
    const { data } = await api.post('/forms/bulk-delete', { ids })
    return data.data as { deleted: number; skipped: number }
  },

  async responses(id: number, params?: { page?: number; limit?: number }) {
    const { data } = await api.get(`/forms/${id}/responses`, { params })
    return data.data as ResponsesPageType
  },

  async deleteResponse(formId: number, responseId: number) {
    const { data } = await api.delete(`/forms/${formId}/responses/${responseId}`)
    return data.data as boolean
  },

  /** Descarga el .xlsx generado por el servidor. */
  async exportResponses(id: number, filename = `respuestas-${id}.xlsx`) {
    const { data } = await api.get(`/forms/${id}/responses/export`, { responseType: 'blob' })
    const url = URL.createObjectURL(data as Blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    // El enlace tiene que estar en el documento para que Firefox lo active, y
    // la URL no se puede liberar en el mismo tick o la descarga sale vacía.
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  },

  // ── Público (sin autenticar) ──────────────────────────────────────────────

  async getPublic(slug: string) {
    const { data } = await api.get(`/public/forms/${slug}`)
    return data.data as Omit<FormType, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>
  },

  async submit(slug: string, answers: Record<string, unknown>) {
    const { data } = await api.post(`/public/forms/${slug}/responses`, { answers })
    return data.data as { id: number; successMessage: string | null }
  }
}
